import { useCallback, useRef } from "react";
import {
  NodeChange,
  EdgeChange,
  Connection,
  NodeDragHandler,
  useReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import { CustomNode, CustomEdge } from "../types";
import { useHistory } from "./useHistory";

interface UseCanvasReturn {
  state: {
    nodes: CustomNode[];
    edges: CustomEdge[];
    selectedNodes: string[];
    selectedEdges: string[];
  };
  onNodesChange: (changes: NodeChange[]) => void;
  onNodeDragStart: NodeDragHandler;
  onNodeDrag: NodeDragHandler;
  onNodeDragStop: NodeDragHandler;
  getNode: (id: string) => CustomNode | undefined;
  selectAll: () => void;
  clearSelection: () => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  getConnectedEdges: (nodeId: string) => CustomEdge[];
}

export function useCanvas(): UseCanvasReturn {
  const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();
  const { addToHistory } = useHistory();

  // Use a ref to track if we're currently processing a history update
  const isUpdatingRef = useRef(false);

  // Helper to safely add to history
  const safeAddToHistory = useCallback(() => {
    if (!isUpdatingRef.current) {
      isUpdatingRef.current = true;
      addToHistory({
        nodes: getNodes() as CustomNode[],
        edges: getEdges() as CustomEdge[],
      });
      isUpdatingRef.current = false;
    }
  }, [getNodes, getEdges, addToHistory]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nodes) => applyNodeChanges(changes, nodes) as CustomNode[]);
      // Only add to history for non-selection changes
      if (changes.some((change) => change.type !== "select")) {
        // Use requestAnimationFrame to ensure state is updated
        requestAnimationFrame(() => {
          safeAddToHistory();
        });
      }
    },
    [setNodes, safeAddToHistory],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((edges) => applyEdgeChanges(changes, edges) as CustomEdge[]);
      // Only add to history for non-selection changes
      if (changes.some((change) => change.type !== "select")) {
        requestAnimationFrame(() => {
          safeAddToHistory();
        });
      }
    },
    [setEdges, safeAddToHistory],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((edges) => addEdge(connection, edges) as CustomEdge[]);
      requestAnimationFrame(() => {
        safeAddToHistory();
      });
    },
    [setEdges, safeAddToHistory],
  );

  const onNodeDragStart: NodeDragHandler = useCallback(() => {
    // No need to track drag state separately
  }, []);

  const onNodeDrag: NodeDragHandler = useCallback(() => {
    // No need to track intermediate drag positions
  }, []);

  const onNodeDragStop: NodeDragHandler = useCallback(() => {
    requestAnimationFrame(() => {
      safeAddToHistory();
    });
  }, [safeAddToHistory]);

  const getNode = useCallback(
    (id: string) => {
      return getNodes().find((node) => node.id === id) as
        | CustomNode
        | undefined;
    },
    [getNodes],
  );

  const getConnectedEdges = useCallback(
    (nodeId: string) => {
      return getEdges().filter(
        (edge) => edge.source === nodeId || edge.target === nodeId,
      ) as CustomEdge[];
    },
    [getEdges],
  );

  const selectAll = useCallback(() => {
    setNodes(
      (nodes) =>
        nodes.map((node) => ({ ...node, selected: true })) as CustomNode[],
    );
  }, [setNodes]);

  const clearSelection = useCallback(() => {
    setNodes(
      (nodes) =>
        nodes.map((node) => ({ ...node, selected: false })) as CustomNode[],
    );
    setEdges(
      (edges) =>
        edges.map((edge) => ({ ...edge, selected: false })) as CustomEdge[],
    );
  }, [setNodes, setEdges]);

  return {
    state: {
      nodes: getNodes() as CustomNode[],
      edges: getEdges() as CustomEdge[],
      selectedNodes: getNodes()
        .filter((node) => node.selected)
        .map((node) => node.id),
      selectedEdges: getEdges()
        .filter((edge) => edge.selected)
        .map((edge) => edge.id),
    },
    onNodesChange,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    getNode,
    selectAll,
    clearSelection,
    onEdgesChange,
    onConnect,
    getConnectedEdges,
  };
}
